// src/components/combobox.tsx
import * as React from "react";

import { Check, ChevronsUpDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils";

export type ComboOption = { label: string; value: string };

type SingleProps = {
  multi?: false;
  value: string | null;
  onChange: (next: string | null) => void;
};

type MultiProps = {
  multi: true;
  value: Array<string>;
  onChange: (next: Array<string>) => void;
};

export type ComboboxProps = (SingleProps | MultiProps) & {
  label?: string;
  // Flat list, or an array of groups rendered with separators between them.
  options: Array<ComboOption> | Array<Array<ComboOption>>;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  id?: string;
  name?: string;
  required?: boolean;
  hint?: string;
  state?: "default" | "error" | "success";
  bare?: boolean;
};

export function Combobox(props: ComboboxProps) {
  const {
    label,
    options,
    disabled,
    className,
    buttonClassName,
    id,
    name,
    required,
    hint,
    state,
    placeholder,
    searchPlaceholder,
    bare = false,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const { t } = useTranslation("common");

  const isMulti = props.multi === true;
  const selectedValues: Array<string> = isMulti
    ? props.value
    : props.value
      ? [props.value]
      : [];
  const groups: Array<Array<ComboOption>> =
    options.length > 0 && Array.isArray(options[0])
      ? (options as Array<Array<ComboOption>>)
      : [options as Array<ComboOption>];
  const flatOptions: Array<ComboOption> = groups.flat();
  const selectedOptions = flatOptions.filter((o) =>
    selectedValues.includes(o.value),
  );
  const hasSelection = selectedOptions.length > 0;
  const floated = focused || open || hasSelection;

  function toggle(v: string) {
    if (isMulti) {
      const next = selectedValues.includes(v)
        ? selectedValues.filter((x) => x !== v)
        : [...selectedValues, v];
      props.onChange(next);
    } else {
      const next = v === props.value ? null : v;
      props.onChange(next);
      setOpen(false);
    }
  }

  function triggerDisplay() {
    // Like Input: with a label, the placeholder only shows once the label has
    // floated up — otherwise the two render on top of each other.
    if (!hasSelection) return label && !floated ? null : (placeholder ?? null);
    if (!isMulti) return selectedOptions[0].label;
    return (
      <span className="flex flex-wrap items-center gap-1 overflow-hidden">
        {selectedOptions.map((o) => (
          <span
            key={o.value}
            className="inline-flex items-center gap-1 rounded-full bg-garnet/10 text-garnet text-xs px-2 py-0.5"
          >
            {o.label}
            <button
              type="button"
              aria-label={`Remove ${o.label}`}
              onClick={(e) => {
                e.stopPropagation();
                toggle(o.value);
              }}
              className="hover:opacity-70"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </span>
    );
  }

  return (
    <label
      className={cn(
        "relative block w-full",
        bare ? "h-8" : "h-[50px]",
        className,
      )}
      htmlFor={id}
    >
      <div
        className={cn(
          !bare ? "rounded-md p-[1px] transition-colors" : "transition-colors h-full",
          bare
            ? "ring-bg-transparent"
            : focused || open
              ? "ring-1 ring-bg-gradient-deep-blue"
              : "ring-bg-transparent",
        )}
      >
        <div className="relative h-full">
          {label && (
            <span
              className={cn(
                "z-2 absolute left-3 text-garnet pointer-events-none transition-all duration-150 will-change-transform",
                floated
                  ? "top-[6px] text-[12px] font-medium"
                  : "top-1/2 -translate-y-1/2 text-[14px]",
              )}
            >
              {label}
              {required ? " *" : ""}
            </span>
          )}

          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                id={id}
                name={name}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                disabled={disabled}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                className={cn(
                  "w-full h-full text-left outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed relative",
                  !bare &&
                    "rounded-md bg-fog text-garnet border border-transparent pt-[18px] pb-[5px] px-3 min-h-[50px]",
                  bare && "bg-transparent border-none p-0",
                  buttonClassName,
                )}
              >
                {bare ? (
                  <span
                    className={cn(
                      "flex items-center h-full truncate pr-6 text-sm",
                      "text-garnet",
                    )}
                  >
                    {triggerDisplay()}
                  </span>
                ) : (
                  <span
                    className={cn(
                      "block truncate pr-6",
                      !hasSelection && "text-garnet/50",
                    )}
                  >
                    {triggerDisplay()}
                  </span>
                )}
                <ChevronsUpDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-60" />
              </button>
            </PopoverTrigger>

            <PopoverContent
              className="w-[--radix-popover-trigger-width] p-0 min-w-[298px] mt-1"
              align="start"
              sideOffset={6}
            >
              <Command>
                <CommandInput className="h-9" placeholder={searchPlaceholder} />
                <CommandList>
                  <CommandEmpty>{t("combobox.noResults")}</CommandEmpty>
                  {groups.map((group, groupIdx) => (
                    <React.Fragment key={groupIdx}>
                      {groupIdx > 0 && <CommandSeparator />}
                      <CommandGroup>
                        {group.map((opt) => {
                          const active = selectedValues.includes(opt.value);
                          return (
                            <CommandItem
                              key={opt.value}
                              value={opt.value}
                              onSelect={(v) => toggle(v)}
                              className="cursor-pointer text-white"
                            >
                              {opt.label}
                              <Check
                                className={cn(
                                  "ml-auto h-4 w-4",
                                  active ? "opacity-100" : "opacity-0",
                                )}
                              />
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </React.Fragment>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {hint && (
        <span
          className={cn(
            "mt-1 text-[11px]",
            state === "error" ? "text-red-500" : "text-garnet",
          )}
        >
          {hint}
        </span>
      )}
    </label>
  );
}
